import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import UserContext from 'shared/components/context/user'
import LoginModal from 'shared/components/login-modal'
import { googleAnalyticsEvent, trackPageView } from 'shared/util/analytics'
import LegacyFileMap from './components/legacy-file'
import { useCreationAccess } from './hooks/use-creation-access'
import { getBasePathFromCreationTool, getCreationToolFromPath } from './routes'
import { CreationToolFrame, CreationToolSourceFile } from './types'
import CreationTool from './types/creation-tool'
import CreationToolOutputType from './types/output-type'
import CreationToolStep from './types/steps'
import CreationToolUserRestriction from './types/user-restriction'
import getCreationSteps from './utils/get-creation-steps'
import getUploadCanvas from './utils/get-upload-canvas'

export type CreationToolsContextProps = {
    tool: CreationTool
    sources: CreationToolSourceFile[]
    outputType: CreationToolOutputType
    steps: CreationToolStep[]
    stepFrames: CreationToolFrame[]
    isSupercut: boolean
    currentStep: CreationToolStep | undefined
    currentAudioId: string
    userRestrictionAccess: CreationToolUserRestriction[]
    setFiles: (sources: CreationToolSourceFile[], outputType: CreationToolOutputType, isSupercut?: boolean) => void
    setSources: (sources: CreationToolSourceFile[]) => void
    setOutputType: (outputType: CreationToolOutputType) => void
    setIsSupercut: (isSupercut: boolean) => void
    setCurrentAudioId: (id: string) => void
    saveFrames: (frames: CreationToolFrame[], step: CreationToolStep) => void
    trackEvent: (category: string, action: string, label: string, value?: string) => void
}

export const CreationToolsContext = createContext({} as CreationToolsContextProps)

type Props = {
    children: ReactNode
}

const CreationToolsContextManager = ({ children }: Props) => {
    const { user } = useContext(UserContext)
    const [sources, setSources] = useState<CreationToolSourceFile[]>([])
    const [outputType, setOutputType] = useState<CreationToolOutputType>(CreationToolOutputType.Gif)
    const [steps, setSteps] = useState<CreationToolStep[]>([])
    const [currentAudioId, setCurrentAudioId] = useState<string>('')
    const [isSupercut, setIsSupercut] = useState<boolean>(false)
    const [showLoginModal, setShowLoginModal] = useState<boolean>(false)
    const stepFramesRef = useRef({})
    const userRestrictionAccess = useCreationAccess()
    const { pathname } = useLocation()
    const { push, replace } = useHistory()
    const tool = getCreationToolFromPath(pathname)
    const basePath = getBasePathFromCreationTool(tool)
    const currentStep = steps.find(step => {
        const route = CreationToolStep.route(step)
        return pathname.indexOf(route) > -1
    })

    useEffect(() => {
        // TODO: Make sure sticker maker fires as stickermaker and not gifmaker?
        trackPageView(pathname)
    }, [pathname])

    const setFiles = async (
        sources: CreationToolSourceFile[],
        outputType: CreationToolOutputType,
        isSupercut: boolean = false
    ) => {
        // if the tool requires login, show the login modal
        if (CreationTool.requiresLogin(tool) && !user.loggedIn) {
            setShowLoginModal(true)
            return
        }

        setIsSupercut(isSupercut)
        const steps = getCreationSteps(sources, tool, outputType, isSupercut)
        setSources(sources)
        setOutputType(outputType)
        setSteps(steps)
        stepFramesRef.current = {}

        if (steps.length && (tool === CreationTool.Upload || outputType === CreationToolOutputType.Sticker)) {
            const url = `${basePath}${CreationToolStep.route(steps[0])}`
            isSupercut ? replace(url) : push(url)
        }
    }

    const saveFrames = (frames: CreationToolFrame[], step: CreationToolStep) => {
        if (!stepFramesRef.current) return

        if (step === CreationToolStep.Upload) {
            // if storing frames for upload step, hit getUploadCanvas to scale/clean canvas for upload
            stepFramesRef.current[step] = frames.map(({ canvas, frameCount }) => {
                return { canvas: getUploadCanvas(canvas), frameCount }
            })
        } else {
            stepFramesRef.current[step] = frames
        }
    }

    const trackEvent = (category: string, action: string, label: string, value: string) => {
        // TODO: Add stricter typing to these function params if possible?
        googleAnalyticsEvent({ category, action, label, value })
    }

    return (
        <CreationToolsContext.Provider
            value={{
                tool,
                sources,
                outputType,
                currentAudioId,
                isSupercut,
                steps,
                stepFrames: currentStep ? stepFramesRef.current[currentStep] || [] : [],
                currentStep,
                userRestrictionAccess,
                setFiles,
                setSources,
                setOutputType,
                setIsSupercut,
                setCurrentAudioId,
                saveFrames,
                trackEvent,
            }}
        >
            {children}
            {tool === CreationTool.GifMaker && <LegacyFileMap />}
            {showLoginModal && <LoginModal />}
        </CreationToolsContext.Provider>
    )
}

export default CreationToolsContextManager
